import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type AccountStatus =
  | "active"
  | "on_hold"
  | "suspended"
  | "deactivated"
  | "pending_deletion"
  | "anonymized";

export interface AccountStatusInfo {
  status: AccountStatus;
  reason: string | null;
  changedAt: string | null;
  deletionScheduledAt: string | null;
  queueId: string | null;
  queueScheduledFor: string | null;
}

export function useAccountStatus() {
  const { user } = useAuth();

  return useQuery<AccountStatusInfo | null>({
    queryKey: ["account-status", user?.id],
    enabled: !!user?.id,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!user?.id) return null;
      const [{ data: profile }, { data: queue }] = await Promise.all([
        supabase
          .from("profiles")
          .select("account_status,status_reason,status_changed_at,deletion_scheduled_at")
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("account_deletion_queue")
          .select("id,scheduled_for,status")
          .eq("user_id", user.id)
          .in("status", ["scheduled", "processing"])
          .maybeSingle(),
      ]);

      const status = (profile?.account_status as AccountStatus) ?? "active";
      return {
        status,
        reason: profile?.status_reason ?? null,
        changedAt: profile?.status_changed_at ?? null,
        deletionScheduledAt: profile?.deletion_scheduled_at ?? null,
        queueId: queue?.id ?? null,
        queueScheduledFor: queue?.scheduled_for ?? null,
      };
    },
  });
}
